import React, { useState, useEffect } from 'react';
import { Shield, Award, TrendingUp, Clock, CheckCircle, Star, Users, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { countryCodes } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import PricingSection from '../components/PricingSection';
import TestimonialSlider from '../components/TestimonialSlider';
import ContactPopup from '../components/ContactPopup';
import Counter from '../components/Counter';
import SEO from '../components/SEO';
import ResponsiveCardSlider from '../components/ResponsiveCardSlider';
import { fetchGeolocation, submitLead, getCountryFromGeo, getDialCodeFromCountryCode } from '../services/api';

const LandingPage = () => {
  const { toast } = useToast();
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Fetch geolocation
    const loadGeoLocation = async () => {
      const result = await fetchGeolocation();
      if (result.success && result.data) {
        setGeoData(result.data);
        
        // Set country code based on geolocation
        const countryCode = getCountryFromGeo(result.data);
        const dialCode = getDialCodeFromCountryCode(countryCode, countryCodes);
        
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode
        }));
      }
    };
    
    loadGeoLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit lead to API
      const result = await submitLead(formData, geoData, countryCodes);

      if (result.success) {
        toast({
          title: "Success!",
          description: result.message || "Thank you! Redirecting...",
        });
        
        setFormData({ name: '', email: '', countryCode: '+1', phone: '', message: '' });
        
        // Redirect after a short delay
        setTimeout(() => {
          window.location.href = result.redirect;
        }, 1500);
      } else {
        // Show error from API
        const errorMessage = result.error || "Failed to submit form. Please try again.";
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
        
        // Also show browser alert for visibility
        alert(`Submission Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Network error. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Federal Protection',
      description: 'Nationwide trademark protection through USPTO registration'
    },
    {
      icon: Award,
      title: 'Expert Team',
      description: 'Experienced trademark attorneys and specialists'
    },
    {
      icon: TrendingUp,
      title: '98% Success Rate',
      description: 'Industry-leading approval rate for trademark applications'
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'Quick turnaround on application preparation and filing'
    },
    {
      icon: CheckCircle,
      title: 'Full Service',
      description: 'End-to-end support from search to registration'
    },
    {
      icon: Star,
      title: 'Satisfaction Guaranteed',
      description: 'Backed by thousands of positive client reviews'
    }
  ];
  const currentYear = new Date().getFullYear();
  return (
    <div className="min-h-screen">
      <SEO 
        title="Professional Trademark Registration Services - Secure Your Brand Today"
        description="Expert trademark registration with 98% success rate. Comprehensive search, filing & monitoring included. Get started in 24 hours. Protect your business identity with our proven legal services."
        keywords="trademark registration, register trademark, trademark filing, brand protection, trademark attorney, trademark search, intellectual property, business trademark, trademark application, legal trademark services"
      />
      
      {/* Hero Section with Form */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1758519288480-1489c17b1519?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGxlZ2FsfGVufDB8fHx8MTc2MzUwMjYxOHww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Secure Your Trademark Today
              </h1>
              <p className="text-xl sm:text-2xl text-gray-200 mb-6">
                Professional trademark registration services with expert guidance every step of the way.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-1" />
                  <span className="text-lg">Comprehensive trademark search included</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-1" />
                  <span className="text-lg">Expert preparation and filing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-teal-400 flex-shrink-0 mt-1" />
                  <span className="text-lg">Ongoing monitoring and support</span>
                </li>
              </ul>
            </div>

            {/* Contact Form */}
            <Card className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Protect Your Brand Today</h2>
                <p className="text-gray-600">Fill out the form below and get expert assistance</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Your full name"
                    className="border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="your.email@example.com"
                    className="border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                  <div className="flex gap-2">
                    <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                      <SelectTrigger className="w-32 border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((item) => (
                          <SelectItem key={item.dial} value={item.dial}>
                            {item.dial}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="Phone number"
                      className="flex-1 border-gray-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <Textarea
                    required
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="Tell us about your trademark needs..."
                    rows={3}
                    className="border-gray-300"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Register My Trademark'}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Trademark Protection Matters */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Why Trademark Protection Matters
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Exclusive Rights</h3>
                    <p className="text-gray-600">
                      Gain exclusive rights to use your trademark nationwide. Prevent others from using confusingly similar marks in your industry.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Brand Recognition</h3>
                    <p className="text-gray-600">
                      Build trust and credibility with customers. A registered trademark shows professionalism and commitment to quality.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Business Asset</h3>
                    <p className="text-gray-600">
                      Your trademark is a valuable business asset that appreciates over time. It can be licensed, franchised, or sold.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Legal Protection</h3>
                    <p className="text-gray-600">
                      Access federal courts for enforcement. Stronger legal standing against infringers and counterfeiters.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070"
                alt="Business protection"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-teal-600 text-white p-6 rounded-lg shadow-xl">
                <p className="text-4xl font-bold mb-1"><Counter end={99} suffix="%" /></p>
                <p className="text-sm">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Makes Us Different
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference of working with trademark experts who care about your success
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Personalized Service</h3>
              <p className="text-gray-600 mb-4">
                Every client gets a dedicated case manager who provides personalized attention and regular updates throughout the process.
              </p>
              <div className="text-teal-600 font-semibold">Direct access to your case manager</div>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Fast Turnaround</h3>
              <p className="text-gray-600 mb-4">
                We understand time is valuable. Most applications are filed within 1-3 business days with our streamlined process.
              </p>
              <div className="text-teal-600 font-semibold">Average 24hr response time</div>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="text-5xl mb-4">💰</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Transparent Pricing</h3>
              <p className="text-gray-600 mb-4">
                No hidden fees or surprises. We provide clear, upfront pricing so you know exactly what to expect.
              </p>
              <div className="text-teal-600 font-semibold">All-inclusive packages available</div>
            </Card>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Our Trademark Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive protection with expert guidance at every step
            </p>
          </div>
          
          <ResponsiveCardSlider className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="p-6 hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
                  <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </Card>
              );
            })}
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Process Section - 3 Steps New Design */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Simple 3-Step Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From search to protection in three streamlined steps
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  1
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Comprehensive Search</h3>
                <p className="text-gray-600 leading-relaxed">
                  Our team performs an extensive search to identify potential conflicts and assess the viability of your trademark application.
                </p>
              </Card>
              {/* Connector Arrow - Hidden on mobile */}
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <div className="text-teal-600 text-4xl">→</div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  2
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Application Filing</h3>
                <p className="text-gray-600 leading-relaxed">
                  We prepare and file your trademark application with the USPTO, ensuring all documentation is accurate and complete.
                </p>
              </Card>
              {/* Connector Arrow - Hidden on mobile */}
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <div className="text-teal-600 text-4xl">→</div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                  3
                </div>
              </div>
              <Card className="pt-10 pb-8 px-6 text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 bg-white group-hover:scale-105">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-600 transition-colors duration-300">
                  <svg className="w-10 h-10 text-teal-600 group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Monitoring & Protection</h3>
                <p className="text-gray-600 leading-relaxed">
                  After approval, we monitor your trademark and provide ongoing support to maintain and protect your brand rights.
                </p>
              </Card>
            </div>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection onGetStarted={() => setShowPopup(true)} />

      {/* New Section: Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protecting brands across diverse sectors with specialized expertise
            </p>
          </div>

          <ResponsiveCardSlider className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { icon: '🏥', name: 'Healthcare' },
              { icon: '💻', name: 'Technology' },
              { icon: '🎨', name: 'Creative Arts' },
              { icon: '🍔', name: 'Food & Beverage' },
              { icon: '👔', name: 'Fashion' },
              { icon: '🏢', name: 'Real Estate' },
              { icon: '📚', name: 'Education' },
              { icon: '🎮', name: 'Entertainment' },
              { icon: '💰', name: 'Finance' },
              { icon: '🚗', name: 'Automotive' },
              { icon: '✈️', name: 'Travel' },
              { icon: '🏋️', name: 'Fitness' }
            ].map((industry, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400 hover:scale-105">
                <div className="text-4xl mb-3">{industry.icon}</div>
                <p className="text-sm font-semibold text-slate-900">{industry.name}</p>
              </Card>
            ))}
          </ResponsiveCardSlider>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              Don't see your industry? We work with businesses of all types and sizes.
            </p>
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-teal-600 hover:bg-teal-700 text-white px-10 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Discuss Your Industry →
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialSlider />

      {/* FAQ Section - New Design */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-teal-100 text-teal-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Got Questions?
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about trademark registration
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-2 gap-6">
            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">Q</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    How long does the trademark registration process take?
                  </h3>
                  <p className="text-gray-600">
                    The entire process typically takes 8-12 months from filing to approval. We file most applications within 1-3 business days and keep you updated throughout.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">Q</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    What's included in your trademark services?
                  </h3>
                  <p className="text-gray-600">
                    Comprehensive search, professional filing, USPTO monitoring, office action responses, and ongoing support throughout the entire registration process.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">Q</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    Do I really need to register my trademark?
                  </h3>
                  <p className="text-gray-600">
                    Registration provides nationwide protection, legal presumption of ownership, and exclusive rights to use your mark. It's essential for serious brand protection.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">Q</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    What are the costs involved?
                  </h3>
                  <p className="text-gray-600">
                    Our transparent pricing includes our service fee and government filing fees. We provide a detailed breakdown before you commit. No hidden charges.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">Q</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    Can you guarantee my trademark will be approved?
                  </h3>
                  <p className="text-gray-600">
                    While we can't guarantee approval, our thorough search and expert filing significantly increase success chances. We assess viability before filing.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-white hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold">Q</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    What happens after my trademark is registered?
                  </h3>
                  <p className="text-gray-600">
                    We provide ongoing monitoring and renewal services to maintain your trademark protection. You'll have lifetime support for your registered mark.
                  </p>
                </div>
              </div>
            </Card>
          </ResponsiveCardSlider>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <Button
              onClick={() => setShowPopup(true)}
              variant="outline"
              size="lg"
              className="border-2 border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white px-10 py-6 text-lg font-bold transition-all duration-300"
            >
              Get Your Questions Answered
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Register Your Trademark?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Start protecting your brand today with our expert trademark services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-teal-600 hover:bg-teal-700 text-white px-12 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Register My Trademark
            </Button>
            <Button
              onClick={() => alert('Chat feature coming soon!')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-12 py-6 text-lg transition-all duration-300"
            >
              Chat Now
            </Button>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-slate-900 text-gray-300 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-400">
              © {currentYear} Trademark Resolutions. All rights reserved.
            </div>
            <div className="flex gap-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-teal-400 transition-colors">
                Privacy Policy
              </a>
              <span className="text-gray-600">|</span>
              <a href="/terms-of-service" className="text-gray-400 hover:text-teal-400 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>

      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default LandingPage;